import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import DescriptionBlock from 'Components/_common/CreationForm/DescriptionBlock'
import GameTypeIcon from 'Components/_common/GameTypeIcon'
import PageBackground from 'Components/_common/PageBackground'
import { useIsAdmin } from 'Components/Auth'
import { useCommentsQuery } from 'Queries/Comments'
import {
  useAddContestCommentMutation,
  useAddContestReplyMutation,
  useDeleteContestCommentMutation,
  useDeleteContestCommentReactionMutation,
  useEditContestCommentMutation,
  useReactOnContestCommentMutation,
} from 'Queries/Contests'
import { When } from 'react-if'
import { navigateToEditContestPage } from 'Scenes/navigators'
import Button from 'Theme/Button'
import Chip from 'Theme/Chip'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'

import { transformTextWithThreeDots } from '../../../utils/text-transformation-utils'
import { ArchiveRestoreEvent } from '../../_common/ArchiveEvent/ArchiveRestoreEvent'
import FaqList from '../../_common/FaqList'
import { Comments } from '../../Comments/Comments'
import { BackgroundPlug } from '../../Layout/MainLayout'
import { ArchiveRestoreContestModal } from '../ArchiveRestoreContestModal'
import { ContestAnnouncements } from '../ContestAnnouncements'
import { sortAndFilterRewards } from '../ContestCreate/helpers'
import ContestGameTypes from '../ContestGameTypes'
import ContestJudgesList from '../ContestJudgesList'
import ContestMapsTabs from '../ContestMapsTabs'
import ContestVoteSection from '../ContestMapsVote/ContestVoteSection'
import ContestPrizes from '../ContestPrizes'
import ContestQualificationList from '../ContestQualification/ContestQualificationList'
import ContestRoundInfo from '../ContestRoundInfo'
import ContestRulesInfo from '../ContestRulesInfo'
import ContestStepper from '../ContestStepper'
import ContestWinnersList from '../ContestWinnersList'
import { CONTEST_COMMUNITY_VOTE_LIFE_STATE } from '../utils'
import ContestActions from './ContestActions'
import ContestInvitationPlate from './ContestInvitationPlate'
import ContestsPaymentPlate from './ContestsPaymentPlate'

const ContestSceneDesktop = ({
  background,
  categoryName,
  contest,
  mapPlayers,
  isContestFetched,
  handleLikeContest,
  showSubmitMapButton,
  hasSubmissionMaps,
  handleSubmitMapClick,
  showRoundInfo,
  isCommunityVoteRound,
  contestState,
  isJudge,
  judges,
  communityVoteTimeLeft,
  owner,
  showQualificationList,
  contestId,
  showJudgesMapsVote,
  isContestant,
  isFinishedRound,
  userId,
  isShowWinnersList,
  winners,
  isWinnersFetched,
  isWithCommunityVote,
  showSubmissionsRoundList,
  gameTitle,
  gameTypeNames,
  classes,
  isInvited,
  isPublishedRound,
  isBiggerThanMDScreen,
  currentRoundLimit,
}) => {
  const isAdmin = useIsAdmin()
  const currentUserRewards = winners?.filter((winner) => winner.user_id === userId).map((winner) => winner.rewards)

  return (
    <>
      <BackgroundPlug deps={[background]}>
        <PageBackground
          height={620}
          imageElement={
            <img
              src={background}
              alt="contest-background"
            />
          }
        />
      </BackgroundPlug>
      <Container>
        <Box py={5}>
          <Box
            display="flex"
            alignItems="flex-end"
            style={{
              height: 450,
            }}
            sx={{ marginTop: '110px' }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="baseline"
              gap={'16px'}
              width={'100%'}
            >
              <Chip
                label={
                  <Typography
                    variant="overline"
                    color="text.primary"
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '7px 14px',
                    }}
                  >
                    <FAIcon iconClassName={FA_ICONS.TROPHY} />
                    MAP CONTEST
                  </Typography>
                }
                style={{
                  background: 'rgba(0, 0, 0, 0.25)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: '22px',
                }}
              />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width={'calc(100% - 20px)'}
                marginRight={'20px'}
              >
                <Box
                  display="flex"
                  gap={2}
                  alignItems="center"
                >
                  <GameTypeIcon
                    gameType={categoryName}
                    size="large"
                    className={classes.icon}
                    style={{ width: '40px', height: '40px' }}
                  />
                  <Typography
                    variant="h1"
                    color="text.primary"
                    sx={{ display: 'flex', wordBreak: 'break-word' }}
                  >
                    {transformTextWithThreeDots(contest.title, isBiggerThanMDScreen ? 30 : 15)}
                  </Typography>
                  <Typography
                    variant="span"
                    color="text.primary"
                    fontSize={24}
                  >
                    {mapPlayers || ''}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={'16px'}
                >
                  <Button
                    variant="text"
                    color="inherit"
                    startIcon={
                      <FAIcon
                        iconClassName={FA_ICONS.HEART}
                        color={contest.is_like ? 'secondary.main' : 'text.tertiary'}
                      />
                    }
                    disabled={!isContestFetched}
                    onClick={handleLikeContest}
                  >
                    {contest.likes_total || 0}
                  </Button>

                  {showSubmitMapButton && (
                    <Button
                      variant="contained"
                      color={hasSubmissionMaps ? 'secondary' : 'primary'}
                      startIcon={<FAIcon iconClassName={hasSubmissionMaps ? FA_ICONS.PEN_TO_SQUARE : FA_ICONS.PLUS} />}
                      disabled={!isContestFetched}
                      onClick={handleSubmitMapClick}
                    >
                      {hasSubmissionMaps ? 'Edit submissions' : 'Submit Map'}
                    </Button>
                  )}
                  {(isAdmin || userId === owner.id) && (
                    <>
                      {contest.is_soft_deleted && (
                        <ArchiveRestoreEvent
                          renderRestoreModal={(props) => (
                            <ArchiveRestoreContestModal
                              eventId={contest.id}
                              eventTypeName="contest"
                              withEdit
                              {...props}
                            />
                          )}
                        />
                      )}

                      <ContestActions
                        contentId={contestId}
                        goToEditPage={navigateToEditContestPage}
                        type={'contest'}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
            <ContestStepper contest={contest} />
          </Box>
        </Box>

        <ContestPrizes
          prizeValue={contest.prize_value}
          merchandise={contest.merchandise}
          rewards={sortAndFilterRewards(contest.rewards)}
        />

        <When condition={isInvited && !isFinishedRound && !isPublishedRound}>
          <ContestInvitationPlate
            contest={contest}
            owner={owner}
          />
        </When>

        <When condition={isPublishedRound && !!contest.payment_info}>
          <ContestsPaymentPlate
            contestId={contestId}
            paymentInfo={contest.payment_info}
            contestEndTime={contest.time_end}
            rewards={currentUserRewards}
          />
        </When>

        {showRoundInfo && (
          <ContestRoundInfo
            roundLimits={contest.round_limits}
            contestState={isCommunityVoteRound ? CONTEST_COMMUNITY_VOTE_LIFE_STATE : contestState}
            timeLeft={isCommunityVoteRound || isFinishedRound ? communityVoteTimeLeft : 0}
            judges={judges}
            currentUserId={userId}
            ownerId={owner.id}
          />
        )}

        {showQualificationList && <ContestQualificationList contestId={contestId} />}

        {showJudgesMapsVote && (
          <ContestVoteSection
            roundLimits={contest.round_limits}
            contestId={contestId}
            contestState={contestState}
          />
        )}

        {isCommunityVoteRound && (
          <ContestVoteSection
            contestId={contestId}
            contestState={CONTEST_COMMUNITY_VOTE_LIFE_STATE}
            isContestant={isContestant}
            timeEnd={contest.time_end}
          />
        )}

        {isShowWinnersList && (
          <ContestWinnersList
            contestId={contestId}
            maps={winners}
            isFetched={isWinnersFetched}
            prizeValue={contest.prize_value}
            rewards={contest.rewards}
            isWithCommunityVote={isWithCommunityVote}
          />
        )}

        <Box sx={{ display: 'flex', gap: '30px' }}>
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            width="calc(100% - 280px)"
          >
            <ContestAnnouncements
              contest={contest}
              isJudge={isJudge}
              isContestant={isContestant}
            />

            <DescriptionBlock description={contest.description} />

            <ContestMapsTabs
              state={contestState}
              contestId={contestId}
              roundLimits={contest.round_limits}
              currentRoundLimit={currentRoundLimit}
            />

            <FaqList faqList={contest.faq} />

            <Comments
              useCommentsQuery={useCommentsQuery}
              addCommentMutation={useAddContestCommentMutation}
              addReplyMutation={useAddContestReplyMutation}
              editCommentMutation={useEditContestCommentMutation}
              deleteCommentMutation={useDeleteContestCommentMutation}
              useReactOnCommentMutation={useReactOnContestCommentMutation}
              useDeleteCommentReactionMutation={useDeleteContestCommentReactionMutation}
              id={contestId}
              type={'contests'}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            pt={5}
            width={'250px'}
          >
            <ContestRulesInfo
              gameTitle={gameTitle}
              categoryName={categoryName}
              mapPlayers={mapPlayers}
              submissionDeadline={contest.time_add_maps_end}
              mapsPerAuthor={contest.max_maps_count}
              isRemakesAllowed={contest.is_remakes_allowed}
            />
            <ContestJudgesList
              judges={judges}
              contest={contest}
              contestRelation={contest.relations}
              isJudge={isJudge}
              isPublishedRound={isPublishedRound}
              isOwner={contest.is_owner}
              contestId={contest.id}
              ownerId={contest.owner_id}
            />
            <ContestGameTypes gameTypeNames={gameTypeNames} />
          </Box>
        </Box>
      </Container>
    </>
  )
}

export default ContestSceneDesktop
